import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MessagesSquare,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Bot,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/cn";
import {
  aiFeedbackAdminService,
  type AdminFeedbackSummary,
  type AdminFeedbackDetail,
} from "@/services/aiFeedbackAdminService";

const PAGE_SIZE = 20;

// Classes reprises de <Input> pour les <select> natifs (convention admin).
const SELECT_CLASS =
  "flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const formatDate = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// Dashboard des feedbacks qualité : liste filtrable (avis, recherche
// email/commentaire), drill-down sur la conversation archivée et le code
// généré. Ces retours sont la matière première du futur apprentissage.
export const AiFeedbacksTab: React.FC = () => {
  const [items, setItems] = useState<AdminFeedbackSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<"" | "up" | "down">("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      const result = await aiFeedbackAdminService.list({
        rating: rating || undefined,
        search: search.trim() || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec du chargement");
    } finally {
      setLoading(false);
    }
  }, [rating, search, page]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const openDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      setDetail(await aiFeedbackAdminService.get(id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec du chargement");
    } finally {
      setDetailLoading(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessagesSquare className="h-5 w-5" aria-hidden="true" />
            <CardTitle>Feedbacks sur l'assistant IA</CardTitle>
          </div>
          <CardDescription>
            Évaluations 👍/👎 laissées par les utilisateurs sur les réponses de
            l'assistant, avec la conversation complète archivée au moment du
            retour. Ces cas alimenteront le système d'apprentissage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filtres */}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Rechercher (email, commentaire)…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="max-w-xs"
            />
            <select
              className={SELECT_CLASS}
              value={rating}
              onChange={(e) => {
                setRating(e.target.value as "" | "up" | "down");
                setPage(0);
              }}
              aria-label="Filtrer par avis"
            >
              <option value="">Tous les avis</option>
              <option value="up">👍 Positifs</option>
              <option value="down">👎 Négatifs</option>
            </select>
            <span className="text-sm text-muted-foreground ml-auto tabular-nums">
              {total} feedback{total > 1 ? "s" : ""}
            </span>
          </div>

          {/* Liste */}
          {loading ? (
            <div className="flex items-center justify-center py-10" aria-busy="true">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aucun feedback pour ces critères.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Avis</th>
                    <th className="py-2 pr-4 font-medium">Utilisateur</th>
                    <th className="py-2 pr-4 font-medium">Commentaire</th>
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 font-medium text-right">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => (
                    <tr key={f.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        {f.rating === "up" ? (
                          <Badge
                            variant="outline"
                            className="gap-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-300/50"
                          >
                            <ThumbsUp className="h-3 w-3" /> Positif
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="gap-1 bg-red-500/10 text-red-700 dark:text-red-300 border-red-300/50"
                          >
                            <ThumbsDown className="h-3 w-3" /> Négatif
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="truncate max-w-[200px] inline-block align-bottom">
                          {f.userEmail ?? f.userId}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="truncate max-w-[280px] inline-block align-bottom text-muted-foreground">
                          {f.comment ?? "—"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                        {formatDate(f.createdAt)}
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 cursor-pointer"
                          disabled={detailLoading}
                          onClick={() => openDetail(f.id)}
                        >
                          Voir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xs text-muted-foreground tabular-nums">
                Page {page + 1} / {pageCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                disabled={page >= pageCount - 1 || loading}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down : conversation archivée + code généré */}
      <Dialog
        open={Boolean(detail)}
        onOpenChange={(open) => !open && setDetail(null)}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.rating === "up" ? (
                <ThumbsUp className="h-4 w-4 text-emerald-600" />
              ) : (
                <ThumbsDown className="h-4 w-4 text-red-600" />
              )}
              Feedback de {detail?.userEmail ?? detail?.userId}
            </DialogTitle>
            <DialogDescription>
              {detail && formatDate(detail.createdAt)}
              {detail?.sessionId && ` — session ${detail.sessionId.slice(0, 12)}…`}
            </DialogDescription>
          </DialogHeader>

          {detail?.comment && (
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
                Commentaire
              </p>
              <p className="text-sm whitespace-pre-wrap">{detail.comment}</p>
            </div>
          )}

          {detail?.conversation && detail.conversation.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Conversation archivée
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto rounded-lg border border-border/60 p-3">
                {detail.conversation.map((m, i) => (
                  <div
                    key={m.id ?? i}
                    className={cn(
                      "flex gap-2 text-sm",
                      m.role === "user" ? "flex-row" : "flex-row",
                    )}
                  >
                    {m.role === "user" ? (
                      <User className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    ) : (
                      <Bot className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <p
                      className={cn(
                        "whitespace-pre-wrap leading-relaxed",
                        m.role === "assistant" && "text-muted-foreground",
                      )}
                    >
                      {m.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail?.generatedCode && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Code généré au moment du feedback
              </p>
              <pre className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs overflow-x-auto font-mono">
                {detail.generatedCode}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
